/**
 * Locales index
 * Экспорт всех локалей
 */
import ru from './ru'
import en from './en'
import vn from './vn'
import { routingOnboardingMessages } from './routingOnboarding'
import { agentOnboardingMessages } from './agentOnboarding'
import { agentTicketsMessages } from './agentTickets'
import { agentsAdminMessages } from './agentsAdmin'
import { agentInquiriesMessages } from './agentInquiries'
import { agentTasksMessages } from './agentTasks'
import { companyVerificationsMessages } from './companyVerifications'
import { rcAnalyticsMessages } from './rcAnalytics'
import { messengerShareMessages } from './messengerShare'
import { presentationsAnalyticsMessages } from './presentationsAnalytics'
import { dev1021Messages } from './dev1021'
import { landingsAnalyticsMessages } from './landingsAnalytics'
import { quizFieldsMessages } from './quizFields'
import { dev1082Messages } from './dev1082'
import { dev1080Messages } from './dev1080'
import { dev1084Messages } from './dev1084'

type AnyRecord = Record<string, unknown>

/**
 * Рекурсивное слияние словарей переводов. Объекты сливаются вглубь, прочие
 * значения (строки и т.п.) перекрываются значением из override. Используется,
 * чтобы подмешать feature-строки (routing- и agent-онбординг) поверх базовых
 * локалей, включая переопределение примитива объектом (fields.primaryValueHint).
 */
function deepMerge<T extends AnyRecord>(base: T, override: AnyRecord): T {
  const out: AnyRecord = { ...base }

  for (const [key, value] of Object.entries(override)) {
    const prev = out[key]
    const bothObjects =
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      prev !== null &&
      typeof prev === 'object' &&
      !Array.isArray(prev)

    out[key] = bothObjects ? deepMerge(prev as AnyRecord, value as AnyRecord) : value
  }

  return out as T
}

export const messages = {
  ru: deepMerge(
    deepMerge(
      deepMerge(
        deepMerge(
          deepMerge(
            deepMerge(deepMerge(ru, routingOnboardingMessages.ru), agentOnboardingMessages.ru),
            agentTicketsMessages.ru,
          ),
          agentsAdminMessages.ru,
        ),
        agentInquiriesMessages.ru,
      ),
      deepMerge(
        deepMerge(agentTasksMessages.ru, companyVerificationsMessages.ru),
        rcAnalyticsMessages.ru,
      ),
    ),
    deepMerge(
      deepMerge(
        deepMerge(messengerShareMessages.ru, presentationsAnalyticsMessages.ru),
        dev1021Messages.ru,
      ),
      deepMerge(
        deepMerge(
          deepMerge(landingsAnalyticsMessages.ru, quizFieldsMessages.ru),
          dev1082Messages.ru,
        ),
        deepMerge(dev1080Messages.ru, dev1084Messages.ru),
      ),
    ),
  ),
  en: deepMerge(
    deepMerge(
      deepMerge(
        deepMerge(
          deepMerge(
            deepMerge(deepMerge(en, routingOnboardingMessages.en), agentOnboardingMessages.en),
            agentTicketsMessages.en,
          ),
          agentsAdminMessages.en,
        ),
        agentInquiriesMessages.en,
      ),
      deepMerge(
        deepMerge(agentTasksMessages.en, companyVerificationsMessages.en),
        rcAnalyticsMessages.en,
      ),
    ),
    deepMerge(
      deepMerge(
        deepMerge(messengerShareMessages.en, presentationsAnalyticsMessages.en),
        dev1021Messages.en,
      ),
      deepMerge(
        deepMerge(
          deepMerge(landingsAnalyticsMessages.en, quizFieldsMessages.en),
          dev1082Messages.en,
        ),
        deepMerge(dev1080Messages.en, dev1084Messages.en),
      ),
    ),
  ),
  vn: deepMerge(
    deepMerge(
      deepMerge(
        deepMerge(
          deepMerge(
            deepMerge(deepMerge(vn, routingOnboardingMessages.vn), agentOnboardingMessages.vn),
            agentTicketsMessages.vn,
          ),
          agentsAdminMessages.vn,
        ),
        agentInquiriesMessages.vn,
      ),
      deepMerge(
        deepMerge(agentTasksMessages.vn, companyVerificationsMessages.vn),
        rcAnalyticsMessages.vn,
      ),
    ),
    deepMerge(
      deepMerge(
        deepMerge(messengerShareMessages.vn, presentationsAnalyticsMessages.vn),
        dev1021Messages.vn,
      ),
      deepMerge(
        deepMerge(
          deepMerge(landingsAnalyticsMessages.vn, quizFieldsMessages.vn),
          dev1082Messages.vn,
        ),
        deepMerge(dev1080Messages.vn, dev1084Messages.vn),
      ),
    ),
  ),
}

export type Locale = keyof typeof messages
export type MessageSchema = typeof ru

export const availableLocales: Array<{ code: Locale; name: string; flag: string }> = [
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'vn', name: 'Tiếng Việt', flag: '🇻🇳' },
]
